import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Layers, Plus, Upload, Pencil, Trash2, Copy, Eye, Send, Archive,
  ListChecks, Lock, Calendar, Unlock, MoreVertical, Trophy,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, StatsCard, StatsGrid, EmptyState, SearchInput, ConfirmModal, AppButton,
  Paginator, ActionMenu, ActionMenuItem, ActionMenuDivider,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { seriesListTrail } from "../QuizBank/quizCenterTrail";
import "./TestSeries.scss";

const STATUS_LABELS = { draft: "Draft", published: "Published", archived: "Archived" };
const UNLOCK_LABELS = { open: "Open", scheduled: "Scheduled", sequential: "Sequential" };
const UNLOCK_ICONS = { open: Unlock, scheduled: Calendar, sequential: Lock };
const PAGE_SIZE = 20;
const ALL_BRANCH = { headers: { "X-Branch-Id": "all" } };

const TestSeriesPage = () => {
  const navigate = useNavigate();

  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [filterData, setFilterData] = useState({ statuses: {}, categories: [], totalSeries: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchFilters = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/test-series/filters", ALL_BRANCH);
      setFilterData(data);
    } catch { /* silent */ }
  }, []);

  const fetchSeries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, limit: PAGE_SIZE, sort,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(categoryFilter && { category: categoryFilter }),
      };
      const { data } = await apiClient.get("/test-series", { params, ...ALL_BRANCH });
      setSeries(data.series || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setToast({ show: true, message: "Failed to load test series", type: "error" });
    }
    setLoading(false);
  }, [page, search, statusFilter, categoryFilter, sort]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchSeries(); }, [fetchSeries]);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      setToast({ show: true, message: okMsg, type: "success" });
      fetchSeries();
      fetchFilters();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Action failed", type: "error" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await act(() => apiClient.delete(`/test-series/${deleteTarget._id}`), "Test series deleted");
    setDeleteTarget(null);
  };

  return (
    <div className="test-series-page">
      <PageHeader
        icon={Layers}
        title="Test Series"
        description={`${filterData.totalSeries || 0} total series`}
        breadcrumbs={seriesListTrail()}
        actions={
          <div className="d-flex align-items-center gap-2">
            <AppButton variant="outline" className="d-flex align-items-center gap-2" onClick={() => navigate("/test-series/import")}>
              <Upload size={16} /> Import Excel
            </AppButton>
            <AppButton variant="default" className="d-flex align-items-center gap-2" onClick={() => navigate("/test-series/create")}>
              <Plus size={16} /> Create Series
            </AppButton>
          </div>
        }
      />

      <StatsGrid className="ts-stats-row">
        <StatsCard title="Total" value={filterData.totalSeries || 0} icon={Layers} iconColor="blue" />
        <StatsCard title="Published" value={filterData.statuses?.published || 0} icon={Send} iconColor="emerald" />
        <StatsCard title="Drafts" value={filterData.statuses?.draft || 0} icon={Pencil} iconColor="amber" />
        <StatsCard title="Archived" value={filterData.statuses?.archived || 0} icon={Archive} iconColor="rose" />
      </StatsGrid>

      <div className="ts-toolbar">
        <div className="ts-search">
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search test series..." />
        </div>
        <Form.Select size="sm" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Form.Select>
        <Form.Select size="sm" value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
          <option value="">All Categories</option>
          {(filterData.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </Form.Select>
        <Form.Select size="sm" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="a-z">A → Z</option>
          <option value="z-a">Z → A</option>
        </Form.Select>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
      ) : series.length === 0 ? (
        <EmptyState
          icon={<Layers size={40} />}
          title="No test series yet"
          description={search || statusFilter || categoryFilter
            ? "Try adjusting your filters"
            : "Bundle your assessments into a series — e.g. a NEET or aptitude mock-test pack."}
          action={!search && !statusFilter ? (
            <AppButton variant="default" onClick={() => navigate("/test-series/create")}>
              <Plus size={16} className="me-1" /> Create Series
            </AppButton>
          ) : null}
        />
      ) : (
        <>
          <div className="ts-list">
            {series.map((s) => {
              const UnlockIcon = UNLOCK_ICONS[s.unlockMode] || Unlock;
              return (
                <div className="ts-card" key={s._id}>
                  <div className="ts-card__header">
                    <div className="flex-grow-1">
                      <div className="ts-card__badges">
                        <span className={`ts-status ts-status--${s.status}`}>{STATUS_LABELS[s.status] || s.status}</span>
                        <span className="ts-chip"><UnlockIcon size={12} /> {UNLOCK_LABELS[s.unlockMode] || s.unlockMode}</span>
                        {s.category && <span className="ts-chip ts-chip--cat">{s.category}</span>}
                      </div>
                      <h3 className="ts-card__title" onClick={() => navigate(`/test-series/${s._id}`)}>{s.title}</h3>
                      {s.description && <p className="ts-card__desc">{s.description}</p>}
                    </div>
                    <ActionMenu trigger={<MoreVertical size={16} />}>
                      <ActionMenuItem onClick={() => navigate(`/test-series/${s._id}`)}><Eye size={14} /> View</ActionMenuItem>
                      <ActionMenuItem onClick={() => navigate(`/test-series/edit/${s._id}`)}><Pencil size={14} /> Edit</ActionMenuItem>
                      <ActionMenuItem onClick={() => act(() => apiClient.post(`/test-series/${s._id}/duplicate`), "Series duplicated")}><Copy size={14} /> Duplicate</ActionMenuItem>
                      {s.status === "draft" && (
                        <ActionMenuItem onClick={() => act(() => apiClient.put(`/test-series/${s._id}/publish`), "Series published!")}><Send size={14} /> Publish</ActionMenuItem>
                      )}
                      {s.status === "published" && (
                        <ActionMenuItem onClick={() => act(() => apiClient.put(`/test-series/${s._id}/archive`), "Series archived")}><Archive size={14} /> Archive</ActionMenuItem>
                      )}
                      <ActionMenuDivider />
                      <ActionMenuItem danger onClick={() => setDeleteTarget(s)}><Trash2 size={14} /> Delete</ActionMenuItem>
                    </ActionMenu>
                  </div>
                  <div className="ts-card__meta">
                    <span className="ts-meta-item"><ListChecks size={14} /> {s.testCount || 0} tests</span>
                    {s.leaderboardEnabled && <span className="ts-meta-item"><Trophy size={14} /> Leaderboard</span>}
                    {s.assignedBatches?.length > 0 && (
                      <span className="ts-meta-item">Batches: {s.assignedBatches.map((b) => b.name || b).join(", ")}</span>
                    )}
                    {s.startDate && (
                      <span className="ts-meta-item"><Calendar size={14} /> {new Date(s.startDate).toLocaleDateString()}
                        {s.endDate && ` – ${new Date(s.endDate).toLocaleDateString()}`}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <Paginator page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
          )}
        </>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Test Series"
        message="This removes the series container only — the member tests and student attempts are kept. Continue?"
        confirmText="Delete"
        variant="danger"
      />

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default TestSeriesPage;
